"use client";

import { useEffect, useState } from "react";
import { AgentPartnerModal, MembershipModal } from "./InfoModals";
import AccountChip from "@/components/site/AccountChip";
import { isSignedIn, useCartStore } from "@/lib/cart-store";
import { MemberAvatarImg } from "@/components/site/MemberAvatar";
import { accountsUrl } from "@/lib/account-links";
import { RESORTS } from "./resorts";

// Phase-1b PORT (strangler-fig): the live snowbus.com global home header as a real, hydrated component,
// replacing the JS-dead mirror chrome. Markup + classes reproduce the live header/menu 1:1 so the inlined
// live CSSOM styles it (pixel parity); the hamburger dropdown is real React state (the mirror's was dead).
// W4 FIDELITY (founder walk): the menu now matches live exactly — the Guest User avatar row, per-item icons
// (icon2–icon15, fetched into the mirror), and the full item list incl. `Become A Partner` + `About` (were
// missing) with `Contact` (was "Contact Us"). Items with no href are JS systems not yet ported (Book Travel,
// the category *Info* panels, Buy & Sell, Become A Partner) — inert until their S2/S3 step. The Guest User
// row's login popup is wired in the W6 step.
const ICON = "/_mirror/_next/static/media/";
type MenuItem = {
  label: string;
  href?: string;
  icon?: string;
  external?: boolean;
  /** F128 / F223 — live opens this item as a DIALOG rather than navigating */
  modal?: "membership" | "partner";
};
const MENU: MenuItem[] = [
  { label: "My Trips", href: "/trips", icon: "icon2.09beec18.svg" },
  { label: "Book Travel", icon: "icon10.aef17dd4.svg" },
  {
    label: "Store / Marketplace",
    href: "/deals-and-packages",
    icon: "icon4.5d321544.svg",
  },
  { label: "Buy & Sell", icon: "icon11.b8a3a261.svg" },
  {
    label: "Member Perks",
    href: "/snowclub-perks",
    icon: "icon7.57b3b1b9.svg",
  },
  // WO-GF-001-P1B-S13 · RW3-11 — the founder found "Membership Info" dead. ROOT-CAUSED as NEVER-WIRED,
  // not a regression: RW-15(b) (S10) approved wiring the hamburger *Info* items and S11 wired the four
  // category ones, but Membership sat in the same dead class and was never in that set — and no behavior
  // check asserted it, which is why it could stay dead quietly. (Live agrees it is dead: verified
  // read-only 2026-07-22 — href="javascript:void(0);", click ⇒ no navigation and no modal. So this is the
  // same ratified override as the four above, not a live mismatch.)
  // DESTINATION IS IN-DOM, NEVER INVENTED: the ported /general-information page carries `id="snowclub"` —
  // the SnowClub membership section — so this reuses the W2 "Learn more" precedent exactly
  // (`/general-information#<section id>`), and the target is a build-served page (no-404).
  {
    label: "Membership Info/Signup",
    // F128 (S17) — LIVE OPENS A SNOWCLUB MODAL here. S13/RW3-11 pointed this at
    // /general-information#snowclub because that anchor really exists on the ported page — but an
    // anchor existing is not evidence that a control navigates to it, and the founder's screenshot
    // shows the dialog. `modal` routes it; the href is dropped so the two can't disagree.
    modal: "membership" as const,
    icon: "icon12.dec7f318.svg",
  },
  // RW-15(b) (S10) — FOUNDER OVERRIDE of live-as-SOT (recorded in the S10 room): on live these three are
  // intentional no-op info panels (`javascript:void(0);`). The founder ruled they should navigate to the
  // section pages. The home hamburger carries no mountain context, so they resolve to the WHISTLER defaults
  // — the same precedent as the S7 footer quicklinks — and every target is a build-served page (no-404).
  // ("Mountains Info" is deliberately NOT wired here: it was outside the approved (b) list; one-line add.)
  {
    label: "Transport Info",
    href: "/mountain-whistler/bus-schedule",
    icon: "icon3.3b8e7db7.svg",
  },
  {
    label: "Equipment Rentals Info",
    href: "/mountain-whistler/equipment-rentals",
    icon: "icon13.1acfcc1a.svg",
  },
  {
    label: "Activities Info",
    href: "/mountain-whistler/activities",
    icon: "icon14.6e1476ce.svg",
  },
  // RW-15(b) follow-up, ratified 2026-07-22 — "Mountains Info" wired on the same basis as the other three
  // (live serves it as a `javascript:void(0);` no-op; founder override → the mountain directory page).
  {
    label: "Mountains Info",
    href: "/mountain-whistler/profile",
    icon: "icon15.610fc162.svg",
  },
  { label: "FAQ", href: "/faq", icon: "icon6.65e48e29.svg" },
  // F223 (S35, founder walk 2026-07-27) — "Become a partner must open its POP-UP; it currently routes to
  // the FAQ." Both halves check out. RW3-11 wired this to `/general-information#become-a-partner` on the
  // anchor-exists inference, and /general-information IS the info/FAQ surface the founder landed on.
  //
  // THE POP-UP WAS ALREADY BUILT — this is a wiring defect, not a missing feature. F127 (S17) shipped
  // `AgentPartnerModal` from the founder's own screenshot of live's business-signup dialog, and
  // `LoginPopup` opens it from the hamburger's "Agent & Partner" row. "Become A Partner" is the SAME
  // control under live's other label, so it routes to the SAME modal. One implementation, two entry
  // points — a second partner dialog would be exactly the drift F234/F235 exist to stop.
  //
  // This also completes the correction InfoModals.tsx §16 already recorded: RW3-11's "no modal on live"
  // observation was superseded by the founder's screenshots for Membership and Agent & Partner. This row
  // was the last one still standing on the retracted reading.
  {
    label: "Become A Partner",
    modal: "partner" as const,
    icon: "icon8.43aa9cd9.svg",
  },
  { label: "About", href: "https://snowgroup.ca", external: true },
  { label: "Contact", href: "/contact-us" },
];

// Hidden SEO nav the live header carries on content/info pages (display:none — zero pixels, load-bearing
// for link equity to the 30 mountain profiles). Captured live 2026-07-19.
// pass 10: this file used to re-declare the 30 entries that `./resorts` already held — a duplicate of the
// app's canonical list. It now imports RESORTS from `./resorts`, the same module page.tsx and the restored
// <ResortSitemap/> band read. One copy, one place. Do not re-inline it here: two copies is how
// `mountain-lake-louis` (no final 'e') quietly drifts into a 404 on a URL that ranks.

export default function SiteHeader({
  resortSitemap = false,
}: {
  resortSitemap?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // F128 — live opens Membership Info as a dialog, not a navigation.
  // F223 — ONE piece of state for both info dialogs. Two independent booleans could both be true, which
  // is a state the menu can never legitimately be in (one item is clicked at a time).
  const [infoModal, setInfoModal] = useState<"membership" | "partner" | null>(null);
  // W6 — chrome sign-in. `mounted` guards against a hydration mismatch: SSR + first client render show
  // signed-out (persisted session hydrates after mount), then reflect the real session.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const setAuthOpen = useCartStore((s) => s.setAuthOpen);
  const session = useCartStore((s) => s.session);
  const signedIn = mounted && isSignedIn(session); // a bearer session OR a server-verified cookie-backed SSO session
  // RW-9 / RW-10 (S10) — auth-aware "My Tickets" (header) + "My Trips" (hamburger). Founder re-walk found
  // that clicking either while signed in rendered the GUEST /trips lookup and looked signed-out. Root cause
  // (verified: the store session + localStorage survive the nav — no actual sign-out): /trips is the guest
  // order-lookup mirror (correct for signed-OUT), but a signed-IN user's tickets live in the accounts app.
  // So: signed-in → the user's orders (accounts app link-out); signed-out → /trips (faithful to live). The
  // session is untouched. SSR/first render = signed-out (mounted guard) → /trips, then reflects the session.
  const ticketsHref = signedIn ? accountsUrl("/my-orders") : "/trips";
  return (
    <>
      {resortSitemap && (
        <nav
          aria-label="Resorts sitemap"
          style={{ display: "none" }}
          className="resort-sitemap"
        >
          <ul>
            {RESORTS.map((r) => (
              <li key={r.href}>
                <a href={r.href}>{r.label}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}
      <header className="css-snowbus homeHeader">
        <nav className="topBar d-flex justify-content-between align-items-center false false">
          <div className="MuiBox-root css-1xhj18k">
            <a href="/" className="mobLogo">
              <img
                alt="Snow Icon"
                width={49}
                height={56}
                className="firstLogo"
                style={{ color: "transparent" }}
                src="/_next/static/media/headerLogoNew2.713156d5.svg"
              />
              <img
                alt="Snow Icon"
                width={128}
                height={31}
                className="secondLogo"
                style={{ color: "transparent" }}
                src="/_next/static/media/snwBus.660369e6.svg"
              />
            </a>
            <span style={{ display: "none" }}>BETA DEMO 1.0</span>
          </div>
          <div className="rightMenu d-flex justify-content-between align-items-center">
            <AccountChip />
            <a className="ticketLink" href={ticketsHref} data-behavior="my-tickets">
              MY TICKETS
            </a>
            {/* FOUNDER-WALK-QA-STAGING-007 · A6 — THE SECOND "SIGN IN", WHICH WAS ONLY EVER A LABEL.
                Signed in, this header rendered `AccountChip` (the customer's name + "Sign out")
                and, immediately beside it, this control still announcing itself as "Sign in" —
                two contradictory statements about the same session, side by side.
                The CONTROL was never wrong: `LoginPopup` already routes it to the account menu
                when a session exists. Only the label lied, so only the label is changed. Removing
                the control would have taken the account menu away with it.
                `signedIn` carries the existing `mounted` guard, so SSR and first paint say "Sign
                in" and it settles after the persisted session hydrates — the same sequence
                `ticketsHref` above already follows, and the reason this is not a hydration risk. */}
            <div
              className="clickProfile"
              data-behavior="signin-open"
              role="button"
              aria-label={signedIn ? "Account menu" : "Sign in"}
              onClick={() => setAuthOpen(true)}
              style={{ cursor: "pointer" }}
            >
              <MemberAvatarImg alt="Profile Icon" width={36} height={36} style={{ color: "transparent" }} fallback="/_next/static/media/profileIcon.4bf36b73.svg" />
            </div>
            <div className="menuIconContaner hasCartNoIcon">
              <div className="dropdown">
                <button
                  type="button"
                  data-behavior="nav-toggle"
                  aria-expanded={open}
                  onClick={() => setOpen((o) => !o)}
                  style={{ fontSize: 0 }}
                  className="dropdown-toggle btn btn-link nodec"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="36"
                    height="36"
                    fill="currentColor"
                    className="bi bi-list MenuIcon"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
                    />
                  </svg>
                </button>
                <div
                  data-behavior="nav-menu"
                  className={"dropdown-menu" + (open ? " show" : "")}
                  style={{
                    display: open ? "block" : "none",
                    position: "absolute",
                    right: 0,
                    zIndex: 1000,
                  }}
                >
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setOpen(false)}
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "12px",
                      zIndex: 1,
                    }}
                  />
                  {/* Guest User / login-or-signup row — opens the W6 login popup; reflects the session. */}
                  <a
                    href="#"
                    className="profileIconContainer dropdown-item"
                    data-behavior="signin-open"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpen(false);
                      setAuthOpen(true);
                    }}
                  >
                    <div className="drpProfileContainer">
                      <MemberAvatarImg alt="" width={36} height={36} fallback={ICON + "profileIcon.4bf36b73.svg"} style={{ color: "transparent" }} />
                      <div className="clickProfile">
                        <p className="profileTextContainer">
                          {signedIn ? session.email : "Guest User"}
                          <span>
                            {signedIn ? "View account" : "login or signup"}
                          </span>
                        </p>
                      </div>
                    </div>
                  </a>
                  {MENU.map((m, i) => {
                    const inner = m.icon ? (
                      <div className="d-flex align-items-center">
                        <div className="dropdown-item-image">
                          <img
                            alt=""
                            width={26}
                            height={26}
                            src={ICON + m.icon}
                            style={{ color: "transparent" }}
                          />
                        </div>
                        {m.label}
                      </div>
                    ) : (
                      m.label
                    );
                    // RW-10 (S10) — "My Trips" is auth-aware, same rule as the header MY TICKETS link.
                    const href = m.label === "My Trips" ? ticketsHref : m.href;
                    const behavior =
                      m.label === "My Trips" ? "my-tickets" : undefined;
                    if (href) {
                      return m.external ? (
                        <a
                          key={i}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dropdown-item"
                        >
                          {inner}
                        </a>
                      ) : (
                        <a
                          key={i}
                          href={href}
                          data-behavior={behavior}
                          className="dropdown-item"
                        >
                          {inner}
                        </a>
                      );
                    }
                    // F128 / F223 — items live opens as a DIALOG. Generic over `modal` so a third one
                    // cannot be added as a fourth hand-rolled branch.
                    const asModal = (m as { modal?: "membership" | "partner" }).modal;
                    if (asModal) {
                      return (
                        <a
                          key={i}
                          href="#"
                          className="dropdown-item"
                          data-behavior={`${asModal}-modal`}
                          onClick={(e) => {
                            e.preventDefault();
                            setInfoModal(asModal);
                          }}
                        >
                          {inner}
                        </a>
                      );
                    }
                    // JS-action item (Buy & Sell, Become A Partner) — inert, exactly as
                    // live serves them (href="javascript:void(0);"). RW-11 (ratified 2026-07-22): "Book Travel"
                    // is tagged so the landing funnel can wire it to the TRANSPORT funnel — live's href="#" is
                    // live's own defect and the founder overrides it. The funnel layer owns the behaviour, so
                    // no new machinery here (it reuses RW-1: popup → pick mountain → /…/bus-schedule).
                    return (
                      <a
                        key={i}
                        href="#"
                        className="dropdown-item"
                        data-behavior={
                          m.label === "Book Travel" ? "book-travel" : undefined
                        }
                        onClick={(e) => e.preventDefault()}
                      >
                        {inner}
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
      {infoModal === "membership" && <MembershipModal onClose={() => setInfoModal(null)} />}
      {/* F223 — the SAME AgentPartnerModal the hamburger's "Agent & Partner" row opens (LoginPopup). */}
      {infoModal === "partner" && <AgentPartnerModal onClose={() => setInfoModal(null)} />}
    </>
  );
}
